/**
 * Hazard catalogue (SPEC_Hazard_UI §4) — 25 entries, mostly read-only.
 *
 * The detail view is the reverse direction on purpose: all types carrying a
 * hazard, split by source and barrier. That is the view gaps show up in —
 * "Absturz: 12 types, but the escalator is missing" — which is why it is the
 * detail rather than a separate report.
 */

import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Search } from "lucide-react";

import { useModalContext } from "../../components/ModalProvider";
import { useLegalAdministrationAPI } from "../../LegalAdministrationAPI";
import { DAMAGE_TYPES, damageTypeLabel } from "./hazardDisplay";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Properties {
  refreshSignal: number;
  onOpenType?: (regulatedObjectCode: string) => void;
}

/** Zwei Spalten „Als Quelle“ / „Als Barriere“ — der Schnitt der Detailansicht. */
function RelationColumn(properties: {
  title: string;
  entries: any[];
  onOpenType?: (regulatedObjectCode: string) => void;
}) {
  return (
    <div className="flex min-w-0 flex-col">
      <div className="border-b pb-1 font-semibold">
        {properties.title} ({properties.entries.length})
      </div>
      <div className="mt-2 flex min-w-0 flex-col gap-1.5">
        {properties.entries.length === 0 ? (
          <span className="text-sm text-muted-foreground">—</span>
        ) : (
          properties.entries.map((entry: any) => (
            <div key={entry.regulatedObjectCode} className="min-w-0 break-words text-sm">
              <button
                type="button"
                className="text-left text-primary underline underline-offset-2 hover:no-underline"
                onClick={() => properties.onOpenType?.(entry.regulatedObjectCode)}
              >
                {entry.name}
              </button>
              {entry.din276?.length ? (
                <span className="text-xs text-muted-foreground"> · {entry.din276.join(", ")}</span>
              ) : null}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function HazardCatalogPanel(properties: Properties) {
  const { t } = useTranslation();
  const { pushError, pushSuccess } = useModalContext() as any;
  const legalAPI = useLegalAdministrationAPI();
  const searchId = useId();

  const [hazards, setHazards] = useState<any[]>([]);
  const [permissions, setPermissions] = useState<any>(null);
  const [search, setSearch] = useState("");
  const [openCode, setOpenCode] = useState<string | null>(null);
  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  // Peek at the assigned types without leaving the list — the full detail view
  // is one click further and shows the same split.
  const [typesDialogFor, setTypesDialogFor] = useState<any>(null);
  const [typesDialogDetail, setTypesDialogDetail] = useState<any>(null);
  const [deprecateDialogFor, setDeprecateDialogFor] = useState<any>(null);

  const load = async () => {
    try {
      setLoading(true);
      const result = await legalAPI.getHazards();
      setHazards(result?.hazards || []);
      setPermissions(result?.permissions || null);
    } catch (error) {
      pushError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties.refreshSignal]);

  useEffect(() => {
    if (!openCode) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await legalAPI.getHazardDetail(openCode);
        if (!cancelled) setDetail(result);
      } catch (error) {
        if (!cancelled) {
          pushError(error);
          setOpenCode(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCode]);

  useEffect(() => {
    if (!typesDialogFor) {
      setTypesDialogDetail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await legalAPI.getHazardDetail(typesDialogFor.code);
        if (!cancelled) setTypesDialogDetail(result);
      } catch (error) {
        if (!cancelled) pushError(error);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typesDialogFor]);

  const deprecate = async (code: string, deprecated: boolean) => {
    try {
      const result = await legalAPI.deprecateHazard(code, deprecated);
      // The server refuses while confirmed assignments exist and reports how
      // many are affected, so the decision is informed rather than guessed.
      await load();
      if (openCode === code) {
        setDetail(await legalAPI.getHazardDetail(code));
      }
      pushSuccess(
        deprecated
          ? t("hazard.catalog.deprecatedSuccess", { code })
          : t("hazard.catalog.reactivatedSuccess", { code }),
      );
      return result;
    } catch (error) {
      pushError(error);
    }
  };

  const filtered = hazards.filter((hazard: any) => {
    if (!search.trim()) return true;
    const needle = search.trim().toLowerCase();
    return (
      (hazard.label || "").toLowerCase().includes(needle) ||
      (hazard.code || "").toLowerCase().includes(needle) ||
      (hazard.description || "").toLowerCase().includes(needle)
    );
  });

  if (openCode) {
    if (!detail) {
      return (
        <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-3">
          <Skeleton className="h-9 w-[200px]" />
          <Skeleton className="h-6 w-[320px] max-w-full" />
          <Skeleton className="h-[180px] w-full" />
        </div>
      );
    }

    const { hazard, carriedBy, protectedBy } = detail;
    const unassigned = (carriedBy?.length || 0) + (protectedBy?.length || 0) === 0;

    return (
      <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
        <div className="flex flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            className="gap-2 px-2 max-sm:w-full max-sm:justify-start"
            onClick={() => setOpenCode(null)}
          >
            <ArrowLeft aria-hidden className="h-4 w-4" />
            {t("hazard.catalog.backToCatalog")}
          </Button>
        </div>

        <div className="flex min-w-0 flex-col gap-1">
          <h3 className="min-w-0 break-words text-lg font-semibold">
            {hazard.code} · {hazard.label}
          </h3>
          <p className="min-w-0 break-words text-sm text-muted-foreground">
            {damageTypeLabel(t, hazard.damageType)}
            {hazard.description ? ` · ${hazard.description}` : ""}
            {hazard.deprecated ? ` · ${t("hazard.catalog.deprecated")}` : ""}
          </p>
        </div>

        {unassigned ? (
          // Not an empty state but a finding, and it should look like one (§9).
          <Alert variant="destructive">
            <AlertDescription>{t("hazard.catalog.notAssignedToAnyType")}</AlertDescription>
          </Alert>
        ) : null}

        <div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-6">
          <RelationColumn
            title={t("hazard.catalog.carriedBy")}
            entries={carriedBy || []}
            onOpenType={properties.onOpenType}
          />
          <RelationColumn
            title={t("hazard.catalog.protectedBy")}
            entries={protectedBy || []}
            onOpenType={properties.onOpenType}
          />
        </div>

        {permissions?.canCurate ? (
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex flex-wrap">
              <Button
                variant="outline"
                className="max-sm:w-full"
                onClick={() => setDeprecateDialogFor(hazard)}
              >
                {hazard.deprecated
                  ? t("hazard.catalog.reactivate")
                  : t("hazard.catalog.deprecate")}
              </Button>
            </div>
            <p className="min-w-0 break-words text-xs text-muted-foreground">
              {t("hazard.catalog.deprecateHint", {
                count: (carriedBy?.length || 0) + (protectedBy?.length || 0),
              })}
            </p>
          </div>
        ) : null}

        <DeprecateDialog
          hazard={deprecateDialogFor}
          assignmentCount={(carriedBy?.length || 0) + (protectedBy?.length || 0)}
          onCancel={() => setDeprecateDialogFor(null)}
          onConfirm={async () => {
            const target = deprecateDialogFor;
            setDeprecateDialogFor(null);
            if (target) await deprecate(target.code, !target.deprecated);
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      <div className="flex min-w-0 flex-wrap items-end gap-4">
        <div className="flex w-[320px] max-w-full flex-col gap-1.5">
          <Label htmlFor={searchId}>{t("hazard.catalog.searchLabel")}</Label>
          <div className="relative">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id={searchId}
              type="search"
              className="pl-9"
              placeholder={t("hazard.catalog.searchPlaceholder")}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>
        <p className="text-sm text-muted-foreground max-sm:w-full sm:pb-2.5">
          {t("hazard.catalog.count", { count: hazards.length })}
        </p>
      </div>

      {loading ? (
        <div className="flex min-w-0 flex-col gap-3">
          <Skeleton className="h-6 w-[220px]" />
          <Skeleton className="h-[200px] w-full" />
          <Skeleton className="h-6 w-[220px]" />
          <Skeleton className="h-[140px] w-full" />
        </div>
      ) : (
        DAMAGE_TYPES.map((damageType) => {
          const group = filtered.filter((hazard: any) => hazard.damageType === damageType);
          if (group.length === 0) return null;
          return (
            <section key={damageType} className="flex min-w-0 flex-col gap-2">
              <h3 className="font-semibold">{damageTypeLabel(t, damageType)}</h3>
              <div className="min-w-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[90px]">{t("hazard.catalog.code")}</TableHead>
                      <TableHead>{t("hazard.catalog.label")}</TableHead>
                      <TableHead>{t("hazard.catalog.effect")}</TableHead>
                      <TableHead className="w-[170px]">
                        {t("hazard.catalog.assignedTypes")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  {/* Stillgelegte Einträge werden benannt, nicht ausgeblasst: eine
                      abgesenkte Deckkraft (Bestand: 0.5) drückte den Zeilentext
                      unter den Kontrastwert. */}
                  <TableBody>
                    {group.map((hazard: any) => (
                      <TableRow key={hazard.code}>
                        <TableCell className="font-mono text-sm">{hazard.code}</TableCell>
                        <TableCell>
                          <button
                            type="button"
                            className="text-left text-primary underline underline-offset-2 hover:no-underline"
                            onClick={() => setOpenCode(hazard.code)}
                          >
                            {hazard.label}
                          </button>
                          {hazard.deprecated ? (
                            <span className="text-xs text-destructive">
                              {" "}
                              · {t("hazard.catalog.deprecated")}
                            </span>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {hazard.description || "—"}
                        </TableCell>
                        <TableCell className="text-sm">
                          {hazard.assignedTypeCount === 0 ? (
                            <span className="text-destructive">{t("hazard.catalog.noTypes")}</span>
                          ) : (
                            <button
                              type="button"
                              className="text-left text-primary underline underline-offset-2 hover:no-underline"
                              title={t("hazard.catalog.showTypes")}
                              onClick={() => setTypesDialogFor(hazard)}
                            >
                              {t("hazard.catalog.typeCount", { count: hazard.assignedTypeCount })}
                              <span className="text-muted-foreground">
                                {" "}
                                ({hazard.carriedByCount}/{hazard.protectedByCount})
                              </span>
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </section>
          );
        })
      )}

      {!loading && filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <p className="text-sm font-semibold">
            {hazards.length === 0
              ? t("hazard.catalog.emptyTitle")
              : t("hazard.catalog.noMatches")}
          </p>
          <p className="max-w-prose text-sm text-muted-foreground">
            {hazards.length === 0
              ? t("hazard.catalog.emptyHint")
              : t("hazard.catalog.noMatchesHint")}
          </p>
          {hazards.length > 0 ? (
            <Button variant="outline" size="sm" onClick={() => setSearch("")}>
              {t("hazard.catalog.resetSearch")}
            </Button>
          ) : null}
        </div>
      ) : null}

      {/* Assigned types at a glance. Split by source and barrier like the detail
          view — a plain count would hide that a third of the entries are the
          types protecting against the hazard, not carrying it. */}
      <Dialog
        open={!!typesDialogFor}
        onOpenChange={(open) => {
          if (!open) setTypesDialogFor(null);
        }}
      >
        <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-[720px]">
          <DialogHeader>
            <DialogTitle className="min-w-0 break-words">
              {typesDialogFor ? `${typesDialogFor.code} · ${typesDialogFor.label}` : ""}
            </DialogTitle>
            <DialogDescription>
              {typesDialogFor
                ? `${damageTypeLabel(t, typesDialogFor.damageType)} · ${t("hazard.catalog.typeCount", {
                    count: typesDialogFor.assignedTypeCount,
                  })}`
                : ""}
            </DialogDescription>
          </DialogHeader>
          {!typesDialogDetail ? (
            <div className="flex min-w-0 flex-col gap-2">
              <Skeleton className="h-5 w-[180px]" />
              <Skeleton className="h-[120px] w-full" />
            </div>
          ) : (
            <div className="grid min-w-0 max-h-[50vh] grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-6 overflow-y-auto">
              {(
                [
                  ["carriedBy", t("hazard.catalog.carriedBy")],
                  ["protectedBy", t("hazard.catalog.protectedBy")],
                ] as const
              ).map(([key, title]) => (
                <RelationColumn
                  key={key}
                  title={title}
                  entries={typesDialogDetail[key] || []}
                  onOpenType={(code: string) => {
                    setTypesDialogFor(null);
                    properties.onOpenType?.(code);
                  }}
                />
              ))}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              className="max-sm:w-full"
              onClick={() => setTypesDialogFor(null)}
            >
              {t("global.close")}
            </Button>
            <Button
              className="max-sm:w-full"
              onClick={() => {
                const code = typesDialogFor?.code;
                setTypesDialogFor(null);
                if (code) setOpenCode(code);
              }}
            >
              {t("hazard.catalog.openDetail")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Stilllegen bzw. Wiederverwenden bestätigen — die Menge ist versioniert. */
function DeprecateDialog(properties: {
  hazard: any;
  assignmentCount: number;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const deprecating = !properties.hazard?.deprecated;

  return (
    <AlertDialog
      open={!!properties.hazard}
      onOpenChange={(open) => {
        if (!open) properties.onCancel();
      }}
    >
      <AlertDialogContent className="w-[calc(100vw-2rem)] sm:max-w-[560px]">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {deprecating ? t("hazard.catalog.deprecate") : t("hazard.catalog.reactivate")}
          </AlertDialogTitle>
          <AlertDialogDescription className="min-w-0 break-words">
            {deprecating
              ? t("hazard.catalog.deprecateConfirm", {
                  label: `${properties.hazard?.code} · ${properties.hazard?.label}`,
                  count: properties.assignmentCount,
                })
              : t("hazard.catalog.reactivateConfirm", {
                  label: `${properties.hazard?.code} · ${properties.hazard?.label}`,
                })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel className="max-sm:w-full">{t("global.cancel")}</AlertDialogCancel>
          <AlertDialogAction className="max-sm:w-full" onClick={properties.onConfirm}>
            {deprecating ? t("hazard.catalog.deprecate") : t("hazard.catalog.reactivate")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
